import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, ArrowUpRight, HeartPulse, Sparkles, Shield, FileText,
  Users, Compass, ChevronDown, Info, X, BookOpen, Menu,
} from "lucide-react";
import Logo from "../components/Logo";

/** Reveal on scroll (identical pattern to AnaPage) */
function useReveal() {
  const containerRef = useRef(null);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const targets = container.querySelectorAll(".reveal");
    if (!("IntersectionObserver" in window) || !targets.length) {
      targets.forEach((n) => n.classList.add("is-visible"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add("is-visible"); io.unobserve(e.target); }
      }),
      { rootMargin: "-8% 0px -8% 0px", threshold: 0.12 }
    );
    targets.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, []);
  return containerRef;
}

const NAV_ITEMS = [
  { id: "overview", label: "Overview" },
  { id: "nurse-experience", label: "Nurse Experience" },
  { id: "ana-integration", label: "ANA Integration" },
  { id: "privacy-doctrine", label: "Privacy & Doctrine" },
  { id: "progress-insights", label: "Progress Insights" },
  { id: "pilot", label: "Pilot" },
];

const DOCUMENTS = [
  { title: "The Future of Nurse Awareness", desc: "Vision paper", href: "/MMG-Future-of-Nurse-Awareness.pdf" },
  { title: "ANA Program Integration", desc: "Program pathways", href: "/MMG-ANA-Program-Integration.pdf" },
  { title: "Behavioral Support Doctrine", desc: "Doctrine framework", href: "/MMG-ANA-Behavioral-Support-Doctrine.pdf" },
];

function StickyNav({ onOpenDocs, activeId }) {
  const [openMobile, setOpenMobile] = useState(false);
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl border-b border-white/5 bg-[#050b1e]/60">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-4 flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={() => { sessionStorage.removeItem("mmg_ana_access"); window.location.assign("/"); }}
          className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white transition text-[12px] opacity-80 flex-shrink-0"
          data-testid="ana-return-main-site"
        >
          <ArrowLeft size={13} /> Return to Main Site
        </button>
        <a
          href="/ana/full"
          className="hidden sm:inline-flex items-center gap-1.5 text-zinc-400 hover:text-white transition text-[12px] opacity-80 flex-shrink-0 pl-3 border-l border-white/8"
          data-testid="ana-goto-full"
        >
          Full Exploration
        </a>
        <div className="hidden lg:flex items-center gap-1.5 text-[12px]">
          {NAV_ITEMS.map((n) => {
            const isActive = activeId === n.id;
            return (
              <a
                key={n.id}
                href={`#${n.id}`}
                className={`px-3 py-1.5 rounded-full transition font-heading ${isActive ? "text-white bg-white/[0.10]" : "text-zinc-400 hover:text-white hover:bg-white/[0.06]"}`}
                data-testid={`ana-nav-${n.id}`}
                aria-current={isActive ? "true" : undefined}
              >{n.label}</a>
            );
          })}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={onOpenDocs}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 border border-white/12 text-zinc-300 hover:text-white hover:border-amber-300/40 text-[12px] transition"
            data-testid="ana-docs-button"
          >
            <BookOpen size={12} /> Documents
          </button>
          <div className="hidden sm:flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-zinc-400 pl-3 border-l border-white/8">
            <Sparkles size={12} className="text-amber-300" /> ANA Concept Portal
          </div>
          <button
            type="button"
            onClick={() => setOpenMobile((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center w-8 h-8 rounded-full border border-white/12 text-zinc-300"
            aria-label="Open navigation"
          >
            {openMobile ? <X size={14} /> : <Menu size={14} />}
          </button>
        </div>
      </div>
      {openMobile && (
        <div className="lg:hidden border-t border-white/5 bg-[#050b1e]/95 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-6 py-3 flex flex-col gap-1">
            {NAV_ITEMS.map((n) => (
              <a
                key={n.id}
                href={`#${n.id}`}
                onClick={() => setOpenMobile(false)}
                className="px-3 py-2 rounded-md text-zinc-300 hover:text-white hover:bg-white/[0.06] text-sm font-heading"
              >{n.label}</a>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}

function DocumentsDrawer({ open, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end" data-testid="ana-docs-drawer">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md h-full bg-[#050b1e] border-l border-white/10 shadow-2xl overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/8">
          <div className="flex items-center gap-2">
            <BookOpen size={16} className="text-amber-300" />
            <div className="font-heading text-white text-[15px]">Supporting Documents</div>
          </div>
          <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-3">
          <p className="text-[12px] text-zinc-400 leading-relaxed mb-4">
            Supporting materials for ANA review. All documents are proposed frameworks, not final commitments.
          </p>
          {DOCUMENTS.map((d) => (
            <a
              key={d.title}
              href={d.href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] hover:border-amber-300/40 hover:bg-white/[0.05] p-4 transition group"
              data-testid={`ana-doc-${d.title.split(" ")[0].toLowerCase()}`}
            >
              <FileText size={16} className="text-amber-300 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-heading text-white text-[14px] leading-tight group-hover:text-amber-200 transition">{d.title}</div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{d.desc}</div>
              </div>
              <ArrowUpRight size={14} className="text-zinc-500 group-hover:text-white transition mt-0.5" />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

function ScreenLabel({ children }) {
  return (
    <div className="text-[11px] uppercase tracking-[0.28em] text-teal-300 mb-3" data-testid="exec-screen-label">
      {children}
    </div>
  );
}

function ExploreLink({ to, children, testId }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1.5 text-[12px] text-amber-200/80 hover:text-white transition font-heading tracking-wide mt-8"
      data-testid={testId}
    >
      {children || "Explore this section in depth"} <ArrowRight size={13} />
    </Link>
  );
}

export default function AnaExecutive() {
  const containerRef = useReveal();
  const [docsOpen, setDocsOpen] = useState(false);
  const [jointOpen, setJointOpen] = useState(false);
  const [activeId, setActiveId] = useState("overview");

  // Scroll-spy: track which sticky-nav section is currently in view
  useEffect(() => {
    const ids = NAV_ITEMS.map((n) => n.id);
    const els = ids.map((id) => document.getElementById(id)).filter(Boolean);
    if (!("IntersectionObserver" in window) || !els.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        // Prefer the entry whose top is closest to the top of viewport
        const visible = entries.filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // Auth guard
  const hasAccess = typeof window !== "undefined" && sessionStorage.getItem("mmg_ana_access") === "granted";
  if (!hasAccess) return <Navigate to="/ana-access" replace />;

  return (
    <div ref={containerRef} className="relative min-h-screen w-full overflow-x-hidden ana-scope bg-[#050b1e] text-white" data-testid="ana-executive-page">
      <style>{`
        .ana-scope { font-family: 'Söhne', 'Inter', system-ui, sans-serif; --font-heading: 'Söhne', 'Inter', system-ui, sans-serif; --font-body: 'Söhne', 'Inter', system-ui, sans-serif; }
        .ana-heading { position: relative; }
        .glass { background: rgba(255,255,255,0.03); backdrop-filter: blur(10px); border: 1px solid rgba(255,255,255,0.06); }
        .reveal { opacity: 0; transform: translateY(24px); transition: opacity 0.9s ease, transform 0.9s ease; }
        .reveal.is-visible { opacity: 1; transform: translateY(0); }
        @media (prefers-reduced-motion: reduce){ .reveal{ opacity: 1; transform: none; transition: none; } }
        html { scroll-behavior: smooth; }
        section[id] { scroll-margin-top: 80px; }
      `}</style>

      <StickyNav onOpenDocs={() => setDocsOpen(true)} activeId={activeId} />
      <DocumentsDrawer open={docsOpen} onClose={() => setDocsOpen(false)} />

      {jointOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6" data-testid="ana-joint-discovery-panel">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setJointOpen(false)} />
          <div className="relative w-full max-w-xl rounded-3xl border border-amber-300/25 bg-[#0a1530] shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-7 py-5 border-b border-white/8">
              <div className="text-[11px] uppercase tracking-[0.28em] text-amber-300">Next proposed step</div>
              <button type="button" onClick={() => setJointOpen(false)} className="text-zinc-400 hover:text-white" aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div className="px-7 py-7">
              <h3 className="font-heading text-white text-2xl leading-tight">Joint Discovery</h3>
              <p className="mt-4 text-zinc-300/90 text-[14px] leading-relaxed">
                A focused working conversation could identify the priority nurse population, meaningful use
                cases, relevant ANA programs, privacy requirements, behavioral standards, technical
                considerations, and pilot success measures.
              </p>
              <p className="mt-4 text-zinc-400 text-[13px] italic leading-relaxed">
                The relationship and next steps would continue through direct discussion with ANA leadership.
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => { setJointOpen(false); document.getElementById("pilot")?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-gradient-to-b from-[#F4EAC8] to-[#E6D19E] text-[#0F1E44] font-heading font-medium text-sm"
                  data-testid="joint-review-pilot"
                >
                  Review the Proposed Pilot <ArrowRight size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => setJointOpen(false)}
                  className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-zinc-300 hover:text-white text-sm transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ambient background glows shared across the page */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute -top-40 -left-20 w-[60vw] h-[60vw] rounded-full bg-blue-500/12 blur-[160px]" />
        <div className="absolute top-1/2 right-0 w-[45vw] h-[45vw] rounded-full bg-sky-400/10 blur-[160px]" />
        <div className="absolute bottom-0 left-1/3 w-[35vw] h-[35vw] rounded-full bg-amber-300/6 blur-[140px]" />
      </div>

      {/* SCREEN 1 — The Proposition (Overview) */}
      <section id="overview" className="relative min-h-[86vh] flex items-center py-24 px-6 sm:px-10 z-10">
        <div className="max-w-6xl mx-auto w-full">
          <div className="reveal is-visible max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass text-[11px] uppercase tracking-[0.28em] text-zinc-300 mb-4">
              <HeartPulse size={12} className="text-rose-300" /> For the American Nurses Association
            </div>
            <p className="font-body text-[12.5px] uppercase tracking-[0.22em] text-teal-300/80 mb-2">
              A Private Awareness Platform for Nurses
            </p>
            <p className="font-body text-[13px] text-zinc-400 leading-relaxed mb-8 max-w-2xl">
              A private concept environment prepared specifically for ANA. This portal presents the proposed
              nurse experience, program integration, privacy architecture, behavioral doctrine, and pilot
              pathway separately from investor materials.
            </p>
            <h1 className="ana-heading font-heading font-medium text-5xl sm:text-6xl lg:text-7xl text-white tracking-tight leading-[1.02]">
              Awareness in the moment. <span className="italic">Evolution over time.</span>
            </h1>
            <p className="mt-6 max-w-2xl font-body text-lg sm:text-xl text-zinc-300/90 leading-relaxed">
              My Mood Groove is designed to help nurses understand what they are carrying, recognize what
              they may need, and connect with relevant support at the moment it matters. It combines
              private conversation, reflection, optional soundscapes, progress insights, and optional
              wearable context within a nurse-controlled experience.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <a href="#nurse-experience" className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-white text-black font-heading font-medium text-sm" data-testid="exec-cta-nurse">
                <ArrowUpRight size={14} /> See the Nurse Experience
              </a>
              <a href="#pilot" className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 glass text-white font-heading font-medium text-sm hover:bg-white/[0.08] transition" data-testid="exec-cta-pilot">
                <FileText size={14} /> Review the Pilot Proposal
              </a>
            </div>
            <div className="mt-16 inline-flex items-center gap-2 text-zinc-500 text-[12px]">
              <ChevronDown size={14} className="animate-bounce" /> Eight brief screens · about three minutes
            </div>
          </div>
        </div>
      </section>

      {/* SCREEN 2 — The Nurse Need */}
      <section id="nurse-experience" className="relative min-h-[86vh] flex items-center py-24 px-6 sm:px-10 z-10">
        <div className="max-w-6xl mx-auto w-full">
          <div className="reveal max-w-4xl">
            <ScreenLabel>The Nurse Need</ScreenLabel>
            <h2 className="ana-heading font-heading font-medium text-4xl sm:text-5xl text-white tracking-tight leading-tight">
              No two nurses carry the same day home.
            </h2>
            <p className="mt-6 font-body text-zinc-300/90 text-lg leading-relaxed max-w-3xl">
              A difficult shift may leave one nurse exhausted, another grieving, another angry, another
              questioning whether they can continue. Their physiological signals may look similar. Their
              lived experiences may be entirely different. The hardest moments often occur between formal
              touchpoints — between surveys, courses, and clinical resources.
            </p>
            <div className="mt-8 rounded-2xl border border-amber-300/25 bg-white/[0.03] p-6 max-w-3xl">
              <p className="font-heading text-white text-xl leading-snug">
                Recognition creates more choices before strain becomes disconnection.
              </p>
            </div>
            <ExploreLink to="/ana/full#nurse-experience" testId="exec-explore-nurse">Explore the full Nurse Experience section</ExploreLink>
          </div>
        </div>
      </section>

      {/* SCREEN 3 — The Differentiation */}
      <section id="differentiation" className="relative min-h-[86vh] flex items-center py-24 px-6 sm:px-10 z-10">
        <div className="max-w-6xl mx-auto w-full">
          <div className="reveal max-w-4xl mb-10">
            <ScreenLabel>The Differentiation</ScreenLabel>
            <h2 className="ana-heading font-heading font-medium text-4xl sm:text-5xl text-white tracking-tight leading-tight">
              The next frontier of digital health is meaning.
            </h2>
          </div>
          <div className="reveal grid sm:grid-cols-3 gap-4">
            {[
              { title: "Activity", body: "What a person did." },
              { title: "Physiology", body: "What the body may be signaling." },
              { title: "Meaning", body: "What may be happening in the person's life.", highlight: true },
            ].map((c) => (
              <div key={c.title} className={`rounded-2xl p-6 border ${c.highlight ? "border-amber-300/40 bg-gradient-to-b from-amber-300/[0.06] to-transparent" : "border-white/10 bg-white/[0.03]"}`}>
                <div className={`text-[11px] uppercase tracking-[0.24em] mb-2 ${c.highlight ? "text-amber-200" : "text-teal-300"}`}>{c.title}</div>
                <p className="text-zinc-100 text-base leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
          <p className="reveal mt-8 text-zinc-300/85 max-w-3xl">
            Wearables can detect change. My Mood Groove helps the nurse understand the personal context surrounding it.
          </p>
          <ExploreLink to="/ana/full#nurse-experience" testId="exec-explore-diff">Explore the Missing Layer in depth</ExploreLink>
        </div>
      </section>

      {/* SCREEN 4 — The Product Experience */}
      <section id="product" className="relative min-h-[86vh] py-24 px-6 sm:px-10 z-10">
        <div className="max-w-6xl mx-auto">
          <div className="reveal max-w-4xl mb-10">
            <ScreenLabel>The Product Experience</ScreenLabel>
            <h2 className="ana-heading font-heading font-medium text-4xl sm:text-5xl text-white tracking-tight leading-tight">
              From signal to understanding to a meaningful next step.
            </h2>
          </div>
          <div className="reveal grid md:grid-cols-5 gap-3">
            {[
              "A signal or personal need appears.",
              "The nurse chooses whether to check in.",
              "The nurse adds personal context.",
              "A relevant experience or support pathway is offered.",
              "Patterns and awareness may develop over time.",
            ].map((step, i) => (
              <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <div className="text-[11px] uppercase tracking-[0.28em] text-amber-300 mb-2">Step {i + 1}</div>
                <p className="text-zinc-100 text-[13.5px] leading-relaxed">{step}</p>
              </div>
            ))}
          </div>
          <div className="reveal mt-10 rounded-2xl border border-amber-300/25 bg-gradient-to-b from-[#0e1d42]/60 to-[#0b1738]/60 p-6 sm:p-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="max-w-xl">
                <div className="text-[11px] uppercase tracking-[0.24em] text-amber-200 mb-2">Live demonstration</div>
                <p className="font-heading text-white text-xl leading-snug">Private. Personal. Controlled by the nurse.</p>
                <p className="text-zinc-300/85 text-sm leading-relaxed mt-2">
                  The Full Route contains two interactive product surfaces: a consent-moment chat on every
                  program card, and a live phone synced to the Awareness Over Time dashboard.
                </p>
              </div>
              <Link
                to="/ana/full#ana-integration"
                className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-white text-black font-heading font-medium text-sm"
                data-testid="exec-open-product"
              >
                Open the Nurse Experience <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
          <ExploreLink to="/ana/full#ana-integration" testId="exec-explore-product">See the interactive experience</ExploreLink>
        </div>
      </section>

      {/* SCREEN 5 — ANA Program Integration */}
      <section id="ana-integration" className="relative min-h-[86vh] flex items-center py-24 px-6 sm:px-10 z-10">
        <div className="max-w-6xl mx-auto w-full">
          <div className="reveal max-w-4xl mb-10">
            <ScreenLabel>ANA Program Integration</ScreenLabel>
            <h2 className="ana-heading font-heading font-medium text-4xl sm:text-5xl text-white tracking-tight leading-tight">
              One private experience. Multiple pathways into ANA support.
            </h2>
            <p className="mt-5 text-zinc-300/90 text-lg leading-relaxed max-w-3xl">
              My Mood Groove does not replace ANA programs. It helps a nurse recognize when an ANA resource
              may be relevant and makes it easier to reach.
            </p>
          </div>
          <div className="reveal grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {[
              { code: "H", name: "Healthy Nurse, Healthy Nation®", desc: "Wellness engagement" },
              { code: "A", name: "American Nurses Foundation", desc: "Research & pilot support" },
              { code: "C", name: "ANCC Excellence", desc: "Practice environment" },
              { code: "W", name: "Nurse Well-Being Initiative", desc: "Sustained support" },
              { code: "P", name: "Pulse on the Nation's Nurses", desc: "Awareness between surveys" },
            ].map((p) => (
              <div key={p.name} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 text-[#0F1E44] font-heading font-semibold flex items-center justify-center text-sm mb-2">{p.code}</div>
                <div className="font-heading text-white text-[13.5px] leading-snug">{p.name}</div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mt-1">{p.desc}</div>
              </div>
            ))}
          </div>
          <div className="reveal mt-8 rounded-2xl border border-amber-300/25 bg-white/[0.03] p-6 max-w-3xl">
            <p className="font-heading text-white text-xl leading-snug">
              A private awareness and navigation layer across the ANA ecosystem.
            </p>
          </div>
          <ExploreLink to="/ana/full#ana-integration" testId="exec-explore-ana">Explore Program Integration in depth</ExploreLink>
        </div>
      </section>

      {/* SCREEN 6 — Trust and Governance */}
      <section id="privacy-doctrine" className="relative min-h-[86vh] flex items-center py-24 px-6 sm:px-10 z-10">
        <div className="max-w-6xl mx-auto w-full">
          <div className="reveal max-w-4xl mb-10">
            <ScreenLabel>Trust and Governance</ScreenLabel>
            <h2 className="ana-heading font-heading font-medium text-4xl sm:text-5xl text-white tracking-tight leading-tight">
              Trust is not a feature. Trust is the foundation.
            </h2>
          </div>
          <div className="reveal grid sm:grid-cols-2 gap-3 max-w-4xl">
            {[
              "Private conversations remain private.",
              "Participation and wearable connection are voluntary.",
              "Personal information is not used for employment or performance decisions.",
              "Only appropriately consented, deidentified, aggregated patterns may contribute to evaluation.",
            ].map((commit, i) => (
              <div key={i} className="rounded-2xl border border-emerald-300/20 bg-white/[0.03] p-5 flex items-start gap-3">
                <Shield size={16} className="text-emerald-300 mt-0.5 flex-shrink-0" />
                <p className="text-zinc-100 text-sm leading-relaxed">{commit}</p>
              </div>
            ))}
          </div>
          <div className="reveal mt-10 rounded-2xl border border-amber-300/30 bg-gradient-to-b from-[#0e1d42]/60 to-[#0b1738]/60 p-6 max-w-3xl">
            <p className="font-heading text-white text-lg italic leading-snug">
              The AI is not the doctrine. The doctrine governs the AI. The AI is not the destination. The nurse is.
            </p>
          </div>
          <ExploreLink to="/ana/full#behavioral-doctrine" testId="exec-explore-doctrine">Explore the Full Behavioral Doctrine</ExploreLink>
        </div>
      </section>

      {/* SCREEN 7 — Value at Three Levels */}
      <section id="progress-insights" className="relative min-h-[86vh] flex items-center py-24 px-6 sm:px-10 z-10">
        <div className="max-w-6xl mx-auto w-full">
          <div className="reveal max-w-4xl mb-10">
            <ScreenLabel>Value at Three Levels</ScreenLabel>
            <h2 className="ana-heading font-heading font-medium text-4xl sm:text-5xl text-white tracking-tight leading-tight">
              Value for the nurse, participating organizations, and ANA.
            </h2>
          </div>
          <div className="reveal grid gap-4 max-w-5xl">
            <div className="rounded-2xl border border-amber-300/35 bg-gradient-to-br from-amber-300/[0.08] to-transparent p-7">
              <div className="text-[11px] uppercase tracking-[0.24em] text-amber-200 mb-2">For the nurse</div>
              <p className="text-zinc-100 text-lg leading-relaxed">
                A private place to decompress, reflect, gain awareness, and identify a meaningful next step
                without feeling watched, scored, or evaluated.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                <div className="text-[11px] uppercase tracking-[0.24em] text-teal-300 mb-2">For participating organizations</div>
                <p className="text-zinc-200 text-sm leading-relaxed">
                  Limited, privacy-protected understanding of broad workforce pressures without access to
                  individual experiences.
                </p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                <div className="text-[11px] uppercase tracking-[0.24em] text-teal-300 mb-2">For ANA</div>
                <p className="text-zinc-200 text-sm leading-relaxed">
                  Continuing connection with nurses between formal programs, while preserving clear privacy
                  boundaries and strengthening access to relevant resources.
                </p>
              </div>
            </div>
          </div>
          <div className="reveal mt-8 rounded-2xl border border-amber-300/25 bg-white/[0.03] p-6 max-w-3xl">
            <p className="font-heading text-white text-lg leading-snug">
              The nurse receives personal value. ANA strengthens program reach. Privacy remains the boundary.
            </p>
          </div>
          <ExploreLink to="/ana/full#ana-aot-section" testId="exec-explore-value">Explore Progress Insights in depth</ExploreLink>
        </div>
      </section>

      {/* SCREEN 8 — The Pilot Invitation */}
      <section id="pilot" className="relative min-h-[86vh] flex items-center py-24 px-6 sm:px-10 z-10">
        <div className="max-w-6xl mx-auto w-full">
          <div className="reveal max-w-4xl mb-10">
            <p className="font-body text-[12.5px] uppercase tracking-[0.22em] text-teal-300/80 mb-3">
              A Practical Next Step
            </p>
            <h2 className="ana-heading font-heading font-medium text-4xl sm:text-5xl text-white tracking-tight leading-tight">
              Begin small. Learn honestly. Earn confidence before scale.
            </h2>
            <p className="mt-6 font-body text-zinc-300/90 text-lg leading-relaxed max-w-3xl">
              A focused discovery followed by a voluntary, limited pilot designed with ANA and participating
              nurses. It evaluates trust, usefulness, privacy, safety, awareness outcomes, engagement,
              resource connection, and optional wearable readiness.
            </p>
          </div>
          <div className="reveal grid sm:grid-cols-3 gap-3 max-w-5xl">
            {[
              { phase: "Phase 1", title: "Joint discovery" },
              { phase: "Phase 2", title: "Controlled pilot" },
              { phase: "Phase 3", title: "Evaluate and determine next steps" },
            ].map((p) => (
              <div key={p.phase} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                <div className="text-[11px] uppercase tracking-[0.28em] text-amber-300 mb-2">{p.phase}</div>
                <div className="font-heading text-white text-lg leading-snug">{p.title}</div>
              </div>
            ))}
          </div>

          {/* Closing reflection — a quiet moment before the ask */}
          <div className="reveal mt-14 mb-4 max-w-3xl mx-auto text-center" data-testid="exec-closing-reflection">
            <div className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-amber-300/40 text-amber-300 text-lg mb-6 mx-auto" aria-hidden>◈</div>
            <p className="font-heading text-white text-xl sm:text-2xl leading-[1.55] tracking-tight text-balance">
              Technology should never replace human understanding. It should deepen it.
            </p>
            <p className="mt-6 font-body text-zinc-300/90 text-[15.5px] leading-relaxed text-balance">
              My Mood Groove was created with the belief that when people become more aware of what is happening
              within themselves, they make healthier decisions, strengthen relationships, and build more
              resilient organizations.
            </p>
            <p className="mt-5 font-heading italic text-amber-100/90 text-lg leading-relaxed text-balance">
              We believe every meaningful partnership begins the same way our conversations do:
              <span className="not-italic text-white"> by listening first.</span>
            </p>
            <p className="mt-7 font-heading text-[11px] uppercase tracking-[0.32em] text-zinc-400" data-testid="exec-closing-signature">
              — The Mood Groove Team
            </p>
          </div>
          <div className="reveal mt-10 flex flex-wrap items-center gap-3">
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 bg-white text-black font-heading font-medium text-sm hover:bg-amber-100 transition"
              data-testid="exec-cta-joint-discovery"
              onClick={() => setJointOpen(true)}
            >
              <Sparkles size={14} /> Advance to Joint Discovery
            </button>
            <Link to="/ana/full#overview" className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 glass text-white text-sm hover:bg-white/[0.08] transition">
              <FileText size={13} /> Read the Vision Paper
            </Link>
            <Link to="/ana/full#ana-integration" className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 glass text-white text-sm hover:bg-white/[0.08] transition">
              <FileText size={13} /> Review Program Integration
            </Link>
            <Link to="/ana/full#behavioral-doctrine" className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 glass text-white text-sm hover:bg-white/[0.08] transition">
              <FileText size={13} /> Explore the Behavioral Doctrine
            </Link>
          </div>
          <ExploreLink to="/ana/full#pilot-proposal" testId="exec-explore-pilot">Explore the Full Pilot Proposal</ExploreLink>
        </div>
      </section>

      {/* Footer — quiet legal & full-route entry */}
      <footer className="relative z-10 border-t border-white/8 py-10 px-6 sm:px-10">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-4">
          <div className="text-[11px] text-zinc-500 leading-relaxed max-w-xl">
            © My Mood Groove. Concept materials prepared for ANA review. ANA, ANCC, Healthy Nurse Healthy Nation®,
            and related program names are used for identification only. This portal is not endorsed by ANA.
          </div>
          <Link
            to="/ana/full"
            className="inline-flex items-center gap-1.5 text-[12px] text-amber-200/80 hover:text-white transition font-heading"
            data-testid="ana-open-full-route"
          >
            Open the full exploration route <ArrowRight size={13} />
          </Link>
        </div>
      </footer>
    </div>
  );
}
